import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, ApiError, type SetupStatus, type User } from "./api";
import { Chat } from "./Chat";
import { TareasPendientes } from "./TareasPendientes";
import { MemoriaPanel } from "./Memoria.js";
import { Control } from "./Control";
import { Calendario } from "./Calendario";
import { Contenidos } from "./Contenidos";
import { Arena } from "./Arena";
import { LeadslinkPanel } from "./Leadslink";
import { ContabilidadPanel } from "./Contabilidad";
import { Mando } from "./Mando";
import { Encargar } from "./Encargar";
import { pedirCliente } from "./ir-al-cliente";

type Pantalla =
  | { tipo: "cargando" }
  | { tipo: "setup"; requiereCodigo: boolean }
  | { tipo: "login" }
  | { tipo: "dentro"; usuario: User };

export default function App() {
  const [pantalla, setPantalla] = useState<Pantalla>({ tipo: "cargando" });

  useEffect(() => {
    void (async () => {
      try {
        // Si ya hay sesión, no se pregunta nada más.
        const { user } = await api.me();
        setPantalla({ tipo: "dentro", usuario: user });
        return;
      } catch {
        /* sin sesión: se sigue comprobando */
      }

      try {
        const estado: SetupStatus = await api.setupStatus();
        setPantalla(
          estado.needsSetup
            ? { tipo: "setup", requiereCodigo: estado.requiresCode }
            : { tipo: "login" },
        );
      } catch {
        setPantalla({ tipo: "login" });
      }
    })();
  }, []);

  if (pantalla.tipo === "cargando") {
    return (
      <div className="centro">
        <p className="cargando">Abriendo BILBOBOT…</p>
      </div>
    );
  }

  if (pantalla.tipo === "dentro") {
    return (
      <Dentro
        usuario={pantalla.usuario}
        alSalir={() => setPantalla({ tipo: "login" })}
      />
    );
  }

  return (
    <div className="centro">
      {pantalla.tipo === "setup" ? (
        <Setup
          requiereCodigo={pantalla.requiereCodigo}
          alEntrar={(usuario) => setPantalla({ tipo: "dentro", usuario })}
        />
      ) : (
        <Login alEntrar={(usuario) => setPantalla({ tipo: "dentro", usuario })} />
      )}
    </div>
  );
}

function Setup({
  requiereCodigo,
  alEntrar,
}: {
  requiereCodigo: boolean;
  alEntrar: (u: User) => void;
}) {
  const [email, setEmail] = useState("");
  const [nombre, setNombre] = useState("");
  const [password, setPassword] = useState("");
  const [repetida, setRepetida] = useState("");
  const [codigo, setCodigo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    setError(null);

    if (password !== repetida) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setEnviando(true);
    try {
      const { user } = await api.createOwner({
        email,
        displayName: nombre,
        password,
        ...(requiereCodigo ? { setupCode: codigo } : {}),
      });
      alEntrar(user);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo crear la cuenta.");
      setEnviando(false);
    }
  }

  return (
    <form className="tarjeta" onSubmit={enviar}>
      <h1 className="marca">BILBOBOT</h1>
      <p className="lema">
        Nadie ha configurado esto todavía. Crea tu cuenta de propietario para empezar.
      </p>

      <div className="nota">
        Este formulario solo existe una vez. En cuanto crees tu cuenta desaparece, y a
        partir de ahí los demás usuarios se invitan desde dentro.
      </div>

      {error && <div className="aviso">{error}</div>}

      <div className="campo">
        <label htmlFor="nombre">Tu nombre</label>
        <input
          id="nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          autoComplete="name"
          required
          autoFocus
        />
      </div>

      <div className="campo">
        <label htmlFor="email">Correo</label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
        />
      </div>

      <div className="campo">
        <label htmlFor="password">Contraseña</label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          minLength={12}
          required
        />
        <p className="pista">Mínimo 12 caracteres.</p>
      </div>

      <div className="campo">
        <label htmlFor="repetida">Repite la contraseña</label>
        <input
          id="repetida"
          type="password"
          value={repetida}
          onChange={(e) => setRepetida(e.target.value)}
          autoComplete="new-password"
          required
        />
      </div>

      {requiereCodigo && (
        <div className="campo">
          <label htmlFor="codigo">Código de instalación</label>
          <input
            id="codigo"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            required
          />
          <p className="pista">
            El servidor lo imprime en su registro al arrancar. Protege la instalación
            mientras no tenga dueño.
          </p>
        </div>
      )}

      <button type="submit" disabled={enviando}>
        {enviando ? "Creando…" : "Crear mi cuenta"}
      </button>
    </form>
  );
}

function Login({ alEntrar }: { alEntrar: (u: User) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      const { user } = await api.login(email, password);
      alEntrar(user);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo entrar.");
      setEnviando(false);
    }
  }

  return (
    <form className="tarjeta" onSubmit={enviar}>
      <h1 className="marca">BILBOBOT</h1>
      <p className="lema">El director digital de la agencia.</p>

      {error && <div className="aviso">{error}</div>}

      <div className="campo">
        <label htmlFor="email">Correo</label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
          autoFocus
        />
      </div>

      <div className="campo">
        <label htmlFor="password">Contraseña</label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
      </div>

      <button type="submit" disabled={enviando}>
        {enviando ? "Entrando…" : "Entrar"}
      </button>
    </form>
  );
}

/**
 * Los dos números que dicen si el sistema está sano, siempre a la vista.
 *
 * Estaban dentro de la pestaña "Hoy", que es donde uno mira cuando ya
 * sospecha. Lo útil es enterarse ANTES: que un ordenador se haya caído o que
 * el reparto esté parado no debería requerir ir a buscarlo.
 *
 * Si la consulta falla no se pinta nada. Una cabecera que dice "0 en línea"
 * porque se perdió la red miente sobre el sistema, y eso es peor que callar.
 */
function EstadoDeCabecera() {
  const [estado, setEstado] = useState<{
    enLinea: number;
    total: number;
    pausado: boolean;
  } | null>(null);

  useEffect(() => {
    let vivo = true;

    async function mirar() {
      try {
        const r = await fetch("/api/hoy", { credentials: "same-origin" });
        if (!r.ok) return;
        const d = (await r.json()) as {
          sistema: { ordenadores: { total: number; enLinea: number }; pausado: boolean };
        };
        if (vivo) {
          setEstado({
            enLinea: d.sistema.ordenadores.enLinea,
            total: d.sistema.ordenadores.total,
            pausado: d.sistema.pausado,
          });
        }
      } catch {
        // Sin red no se toca lo que ya había: se queda el último dato bueno.
      }
    }

    void mirar();
    const t = setInterval(() => void mirar(), 20_000);
    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, []);

  if (!estado) return null;

  const todos = estado.total > 0 && estado.enLinea === estado.total;

  return (
    <div className="estado-cabecera">
      <div className="dato">
        <span className="k">Ordenadores</span>
        <span className="v">
          <span className={todos ? "punto ok" : "punto mal"} aria-hidden="true" />
          {estado.enLinea}/{estado.total}
        </span>
      </div>
      <div className="dato">
        <span className="k">Reparto</span>
        <span className={estado.pausado ? "v parado" : "v"}>
          {estado.pausado ? "Parado" : "En marcha"}
        </span>
      </div>
    </div>
  );
}

/** Las iniciales, para el círculo. Dos como mucho: tres ya no se leen. */
function iniciales(nombre: string): string {
  const partes = nombre.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0]!.slice(0, 2).toUpperCase();
  return (partes[0]![0]! + partes[1]![0]!).toUpperCase();
}

/**
 * Quién ha entrado, y cerrar sesión.
 *
 * Era un botón que ponía "Jorge · salir" y ocupaba sitio para ofrecer algo que
 * se usa una vez al día. Ahora es un círculo con su aura y lo demás se
 * despliega al tocarlo.
 *
 * El aura va en violeta y no en turquesa a propósito: el turquesa es el
 * sistema —el orbe, el estado, los botones— y esto eres tú. Si todo brillara
 * del mismo color, el color dejaría de decir nada.
 */
function MenuDeUsuario({
  usuario,
  salir,
  saliendo,
}: {
  usuario: User;
  salir: () => void;
  saliendo: boolean;
}) {
  const [abierto, setAbierto] = useState(false);

  /**
   * Se cierra al tocar fuera y con Escape.
   *
   * Sin esto queda abierto mientras usas la aplicación por debajo, tapando
   * justo la esquina donde está el estado del sistema.
   */
  useEffect(() => {
    if (!abierto) return;

    function fuera(e: MouseEvent) {
      if (!(e.target as HTMLElement).closest(".menu-usuario")) setAbierto(false);
    }
    function tecla(e: KeyboardEvent) {
      if (e.key === "Escape") setAbierto(false);
    }

    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", tecla);
    };
  }, [abierto]);

  return (
    <div className="menu-usuario">
      <button
        className={abierto ? "avatar abierto" : "avatar"}
        onClick={() => setAbierto((a) => !a)}
        title={usuario.displayName}
        aria-haspopup="menu"
        aria-expanded={abierto}
      >
        {iniciales(usuario.displayName)}
      </button>

      {abierto && (
        <div className="desplegable" role="menu">
          <div className="quien">
            <strong>{usuario.displayName}</strong>
            <span>{usuario.email}</span>
          </div>
          <button className="cerrar-sesion" onClick={salir} disabled={saliendo} role="menuitem">
            {saliendo ? "Cerrando…" : "Cerrar sesión"}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Cuántas tareas esperan decisión, para el número de la pestaña.
 *
 * Se pide a su propio endpoint y no a la lista entera: esto se consulta desde
 * cualquier pantalla y cada poco, y traerse las trece tareas para pintar dos
 * dígitos serían cuatro consultas a la base por un número.
 *
 * Si falla, el número no se pinta y ya está. Un contador que se equivoca es
 * peor que uno que no está: uno manda mirar donde no hay nada, o —peor— deja
 * de mandar cuando sí lo hay.
 */
function useCuantasEsperan(cada = 60_000): number | null {
  const [cuantas, setCuantas] = useState<number | null>(null);
  useEffect(() => {
    let vivo = true;
    const mirar = async () => {
      try {
        const r = await fetch("/api/tareas-pendientes/cuantas", { credentials: "same-origin" });
        if (!r.ok) return;
        const c = (await r.json()) as { ahora?: number };
        if (vivo && typeof c.ahora === "number") setCuantas(c.ahora);
      } catch {
        /* sin número, que es mejor que con uno falso */
      }
    };
    void mirar();
    const t = setInterval(() => void mirar(), cada);
    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, [cada]);
  return cuantas;
}

/** Las pantallas que hay, en el orden en que se usan. */
const PANTALLAS = [
  { id: "chat", nombre: "Chat", icono: "💬" },
  /*
   * Al lado del chat y no al final: es la pantalla donde Jorge decide, y una
   * lista de decisiones en la décima posición es una lista que se mira cuando
   * ya da igual. Lleva contador porque lo que no se ve no espera: se queda.
   */
  { id: "tareas", nombre: "Tareas pendientes", icono: "✅" },
  /* Todo el trabajo en una pantalla (20/10/2026): qué hay, quién, y si está hecho. */
  { id: "mando", nombre: "Mando", icono: "🧭" },
  { id: "control", nombre: "Control", icono: "🎛️" },
  { id: "encargar", nombre: "Encargar", icono: "📤", mas: true },
  { id: "memoria", nombre: "Memoria", icono: "🧠" },
  { id: "contenidos", nombre: "Contenidos", icono: "✍️" },
  { id: "arena", nombre: "Arena", icono: "🏁", mas: true },
  { id: "calendario", nombre: "Calendario", icono: "📅", mas: true },
  { id: "leadslink", nombre: "Leadslink", icono: "📱", mas: true },
  { id: "contabilidad", nombre: "Contabilidad", icono: "💶", mas: true },
] as const;

/*
 * Las que van en «Más» en el ordenador (22/10/2026).
 *
 * Doce pestañas necesitan unos 1.400 píxeles con la marca y el usuario. Por
 * debajo —un portátil de 1.366, una ventana a media pantalla— se pisaban y
 * «Chat» y «Contabilidad» quedaban cortadas. A la vista se quedan las siete de
 * cada día; el resto, en un desplegable. En el móvil no cambia nada: ahí ya
 * van todas en el menú.
 */
const ES_DE_MAS = (p: (typeof PANTALLAS)[number]) => "mas" in p && p.mas === true;

type Vista = (typeof PANTALLAS)[number]["id"];

/**
 * El menú: pestañas en el ordenador, desplegable en el móvil.
 *
 * ── Por qué en el móvil no son pestañas ─────────────────────────────────
 *
 * Porque son siete y no caben. Empezaron siendo cinco y entraban apretadas;
 * al añadir Leadslink y Contabilidad dejaron de entrar, y una fila que no cabe
 * hace una de dos cosas, las dos malas: se comprime hasta partir los nombres
 * en dos líneas —y entonces la cabecera crece, y la altura del chat está
 * calculada como `calc(100% - 57px)`—, o se sale de la pantalla y hay
 * pestañas que no se pueden pulsar.
 *
 * ── Y por qué una sola lista ────────────────────────────────────────────
 *
 * Estaban escritas a mano siete veces, y añadir una pantalla eran siete
 * sitios que tocar y uno que olvidar. Ahora la lista manda: el ordenador la
 * pinta en fila y el móvil la pinta desplegada.
 */
function Pantallas({
  vista,
  ir,
  esperando,
}: {
  vista: Vista;
  ir: (v: Vista) => void;
  /** Cuántas tareas esperan decisión. `null` mientras no se sepa. */
  esperando: number | null;
}) {
  const [abierto, setAbierto] = useState(false);
  /** El desplegable «Más» del ordenador. */
  const [masAbierto, setMasAbierto] = useState(false);
  const caja = useRef<HTMLDivElement | null>(null);
  /** Cuándo fue el último toque válido, para descartar el click fantasma. */
  const ultimoToque = useRef(0);
  const actual = PANTALLAS.find((p) => p.id === vista) ?? PANTALLAS[0];

  /*
   * Se cierra al tocar fuera y con Escape.
   *
   * Sin esto, en el móvil se queda abierto tapando media pantalla y hay que
   * acertar otra vez en el icono para quitarlo — que es justo el momento en
   * que uno cree que la aplicación se ha colgado.
   *
   * ── Dos detalles que en el móvil no son detalles ────────────────────
   *
   * Va por `pointerdown` y no por `mousedown`: un toque en el móvil genera
   * `touchstart`, `touchend`, `mousedown`, `mouseup` y `click`, y algunos
   * navegadores sueltan además un «click fantasma» un rato después. Los
   * eventos de puntero unifican ratón y dedo y llegan UNA vez.
   *
   * Y el oyente se engancha en el fotograma siguiente. Si se enganchara en el
   * mismo, el propio toque que acaba de abrir el menú podía llegar todavía
   * como `pointerdown` y cerrarlo al instante: el menú se abría y se cerraba
   * tan rápido que parecía que no se abría.
   */
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: Event) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAbierto(false);
    };
    const cuando = requestAnimationFrame(() =>
      document.addEventListener("pointerdown", fuera),
    );
    document.addEventListener("keydown", tecla);
    return () => {
      cancelAnimationFrame(cuando);
      document.removeEventListener("pointerdown", fuera);
      document.removeEventListener("keydown", tecla);
    };
  }, [abierto]);

  useEffect(() => {
    if (!masAbierto) return;
    const fuera = (e: Event) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setMasAbierto(false);
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMasAbierto(false);
    };
    const cuando = requestAnimationFrame(() => document.addEventListener("pointerdown", fuera));
    document.addEventListener("keydown", tecla);
    return () => {
      cancelAnimationFrame(cuando);
      document.removeEventListener("pointerdown", fuera);
      document.removeEventListener("keydown", tecla);
    };
  }, [masAbierto]);

  const enMas = ES_DE_MAS(actual);

  return (
    <div className="pantallas" ref={caja}>
      {/* El ordenador: la fila de siempre. */}
      <nav className="pestanas">
        {PANTALLAS.filter((p) => !ES_DE_MAS(p)).map((p) => (
          <button
            key={p.id}
            className={vista === p.id ? "activa" : ""}
            onClick={() => ir(p.id)}
          >
            {p.nombre}
            {p.id === "tareas" && esperando !== null && esperando > 0 && (
              <span className="cuantas-esperan" aria-label={`${esperando} esperando decisión`}>
                {esperando}
              </span>
            )}
          </button>
        ))}
        {/* Si estás en una de «Más», el botón lleva su nombre: dice dónde estás. */}
        <button
          type="button"
          className={`mas${enMas ? " activa" : ""}`}
          aria-expanded={masAbierto}
          aria-haspopup="menu"
          onClick={() => setMasAbierto((x) => !x)}
        >
          {enMas ? actual.nombre : "Más"} <span aria-hidden="true">▾</span>
        </button>
      </nav>

      {/* Fuera del `nav`, que se desliza en horizontal y recortaría el desplegable. */}
      {masAbierto && (
        <div className="menu-mas" role="menu">
          {PANTALLAS.filter(ES_DE_MAS).map((p) => (
            <button
              key={p.id}
              role="menuitem"
              className={vista === p.id ? "activa" : ""}
              onClick={() => {
                ir(p.id);
                setMasAbierto(false);
              }}
            >
              <span aria-hidden="true">{p.icono}</span> {p.nombre}
            </button>
          ))}
        </div>
      )}

      {/*
        El móvil: el icono dice DÓNDE ESTÁS, no sólo que hay un menú.
        Un icono mudo obliga a abrirlo para saber en qué pantalla andas.
      */}
      {/*
        El toggle se ignora si llega dos veces seguidas en el mismo suspiro.
        Un «click fantasma» del móvil abriría y cerraría el menú en el mismo
        toque, y el efecto que se ve es que no se abre.
      */}
      <button
        type="button"
        className="menu-movil"
        onClick={() => {
          const ahora = Date.now();
          if (ahora - ultimoToque.current < 350) return;
          ultimoToque.current = ahora;
          setAbierto((x) => !x);
        }}
        aria-expanded={abierto}
        aria-haspopup="menu"
        aria-label={`Menú. Estás en ${actual.nombre}`}
      >
        <span aria-hidden="true">{actual.icono}</span>
        <span className="donde">{actual.nombre}</span>
        <span className="flecha" aria-hidden="true">
          {abierto ? "▴" : "▾"}
        </span>
      </button>

      {abierto && (
        <div className="menu-pantallas" role="menu">
          {PANTALLAS.map((p) => (
            <button
              key={p.id}
              role="menuitem"
              className={vista === p.id ? "activa" : ""}
              onClick={() => {
                ir(p.id);
                setAbierto(false);
              }}
            >
              <span aria-hidden="true">{p.icono}</span> {p.nombre}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Dentro({ usuario, alSalir }: { usuario: User; alSalir: () => void }) {
  const [saliendo, setSaliendo] = useState(false);
  /*
   * La pantalla con la que se abre, y de dónde sale.
   *
   * Por defecto el chat. Pero el parte del día enlaza a `#contenidos` desde el
   * móvil, y un enlace que promete una pantalla y abre otra es peor que no
   * ponerlo: se lee el hash UNA vez al cargar, y sólo si nombra una pantalla que
   * existe.
   */
  const [vista, setVista] = useState<Vista>(() => {
    /* «Hoy» vive dentro del Mando desde el 22/10/2026: los enlaces viejos a #hoy llevan allí. */
    /*
     * Un enlace desde Trello a un cliente (`?cliente=<id>#memoria`, 26/10/2026): el
     * punto «Auditoría SEO» de cada ficha lleva aquí, a «Cómo va» de ese cliente.
     * Se lee una vez y se quita de la dirección, como `?conversacion=`.
     */
    const delCliente = new URLSearchParams(window.location.search).get("cliente");
    if (delCliente) {
      pedirCliente(delCliente, "como-va");
      window.history.replaceState(null, "", window.location.pathname);
      return "memoria";
    }
    /* Un «Probar» del parte (`?pedir=`) va al chat, que es donde se envía. */
    if (new URLSearchParams(window.location.search).has("pedir")) return "chat";
    const pedida = window.location.hash.replace(/^#/, "").replace(/^hoy$/, "mando");
    return (PANTALLAS.some((p) => p.id === pedida) ? pedida : "chat") as Vista;
  });

  /*
   * El encargo de un «Probar» del parte (30/10/2026): se escribe en una
   * conversación nueva y lo envía Jorge. Se lee una vez y se quita de la
   * dirección, como `?conversacion=`, para que recargar no lo vuelva a escribir.
   */
  const [pedirAlCargar] = useState<string | null>(() => {
    const pedido = new URLSearchParams(window.location.search).get("pedir");
    if (pedido) window.history.replaceState(null, "", window.location.pathname);
    return pedido?.trim() || null;
  });
  const esperando = useCuantasEsperan();

  /*
   * Un enlace a una conversación (`?conversacion=`) abre el chat en ella. Se
   * lee una vez y se quita de la dirección: si no, recargar volvería a ella
   * aunque después se hubiera cambiado de conversación.
   */
  const [abrirAlCargar] = useState<string | null>(() => {
    const id = new URLSearchParams(window.location.search).get("conversacion");
    if (id) window.history.replaceState(null, "", window.location.pathname);
    return id;
  });

  /**
   * Si el historial se ve o no. Empieza cerrado en las dos pantallas.
   *
   * Antes era una columna fija de 250 px en el portátil, y en cuanto bajabas
   * por la conversación no servía para nada y seguía comiendo pantalla. Ahora
   * se despliega por encima cuando hace falta y se quita en cuanto eliges.
   */
  const [historial, setHistorial] = useState(false);

  async function salir() {
    setSaliendo(true);
    try {
      await api.logout();
    } finally {
      alSalir();
    }
  }

  return (
    <>
      <header className="cabecera">
        <div className="izquierda">
          {/* El orbe: el mismo motivo que el fondo de panal, sin logotipo ni
              nombre de la agencia. Es su herramienta, no su web. */}
          <span className="orbe" aria-hidden="true" />
          <strong className="marca-titulo">BILBOBOT</strong>
          {vista === "chat" && (
            <button
              className={`historial ${historial ? "activa" : ""}`}
              onClick={() => setHistorial((abierto) => !abierto)}
              title={historial ? "Ocultar el historial" : "Ver el historial"}
              aria-expanded={historial}
            >
              <span aria-hidden="true">🕘</span> <span className="etiqueta">Historial</span>
            </button>
          )}
        </div>
        {/**
         * Un solo nombre por sitio, en móvil y en ordenador.
         *
         * Había dos juegos de palabras: en pantalla ancha ponía «Conversación»,
         * «Memoria» y «Calendario»; en el móvil, «Chat», «Sabe» y «Fechas». La
         * idea era que cupieran, y el efecto fue que la misma pantalla se
         * llamaba de dos formas según dónde la abrieras — imposible decirle a
         * alguien «entra en Memoria» sin añadir «que en el móvil pone Sabe».
         *
         * Se quedan los nombres cortos y claros. Si alguno no cupiera, la
         * respuesta es achicar la letra, no cambiarle el nombre a la pantalla.
         */}
        <Pantallas vista={vista} ir={setVista} esperando={esperando} />
        {/**
         * Todo lo de la derecha en un solo bloque.
         *
         * Con la cabecera en tres columnas —izquierda, menú, derecha— el menú
         * queda centrado en la pantalla de verdad, no "entre lo que sobra".
         * Antes eran cuatro hijos sueltos y el centro bailaba según lo largo
         * que fuera el nombre de quien había entrado.
         */}
        <div className="derecha">
          {/* Ordenadores en línea y si el reparto está parado: los dos datos
              que cambian si algo va mal, y estaban enterrados en "Hoy". */}
          <EstadoDeCabecera />

          <MenuDeUsuario usuario={usuario} salir={salir} saliendo={saliendo} />
        </div>
      </header>
      {vista === "tareas" ? (
        <TareasPendientes />
      ) : vista === "mando" ? (
        <Mando irA={setVista} />
      ) : vista === "chat" ? (
        <Chat
          usuario={usuario}
          abrirAlCargar={abrirAlCargar}
          pedirAlCargar={pedirAlCargar}
          historialAbierto={historial}
          cerrarHistorial={() => setHistorial(false)}
        />
      ) : vista === "encargar" ? (
        <Encargar />
      ) : vista === "control" ? (
        <Control />
      ) : vista === "memoria" ? (
        <MemoriaPanel irA={setVista} />
      ) : vista === "leadslink" ? (
        <LeadslinkPanel />
      ) : vista === "contabilidad" ? (
        <ContabilidadPanel />
      ) : vista === "contenidos" ? (
        <Contenidos irA={(v) => setVista(v as Vista)} />
      ) : vista === "arena" ? (
        <Arena />
      ) : (
        <Calendario />
      )}
    </>
  );
}
